import React from "react";
import { format } from "date-fns";
import { MoreVertical, Pencil, Route, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MemberAvailabilityStatus from "@/components/team/MemberAvailabilityStatus";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { MemberAvailability } from "@/model/interface/Membership";
import Team from "@/model/interface/Team";
import { getInitialsFromFullName } from "@/util/string";
import { memberName } from "@/util/member";

const MAX_VISIBLE_MEMBERS = 6;

interface TeamCardProps {
  team: Team;
  availabilityByMembershipId: Map<string, MemberAvailability>;
  flowNames: string[];
  onEdit: () => void;
  onDelete: () => void;
}

export default function TeamCard({ team, availabilityByMembershipId, flowNames, onEdit, onDelete }: TeamCardProps) {
  const activeMembers = [...team.members]
    .sort((first, second) => first.position - second.position)
    .filter((member) => member.membership.status === MembershipStatus.ACTIVE);
  const visibleMembers = activeMembers.slice(0, MAX_VISIBLE_MEMBERS);
  const hiddenCount = activeMembers.length - visibleMembers.length;
  const reachableCount = activeMembers.filter((member) => {
    const availability = availabilityByMembershipId.get(String(member.membership.id));
    return availability === "AVAILABLE" || availability === "MOBILE";
  }).length;

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start gap-3 space-y-0 p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary">
          {getInitialsFromFullName(team.name)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{team.name}</div>
          <div className="text-sm tabular-nums text-muted-foreground">
            {activeMembers.length} {activeMembers.length === 1 ? "member" : "members"} · {reachableCount} available
            now
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Options for ${team.name}`} title="Options">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEdit}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={onDelete}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ring order</div>
          {visibleMembers.length === 0 && <p className="text-sm text-muted-foreground">No active members</p>}
          {visibleMembers.map((member, memberIndex) => {
            const name = memberName(member.membership, "Member");
            return (
              <div key={member.id} className="flex items-center gap-2.5">
                <span className="w-4 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {memberIndex + 1}
                </span>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">
                  {getInitialsFromFullName(name)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{name}</span>
                <MemberAvailabilityStatus
                  availability={availabilityByMembershipId.get(String(member.membership.id))}
                />
              </div>
            );
          })}
          {hiddenCount > 0 && <p className="pl-6 text-xs text-muted-foreground">+{hiddenCount} more</p>}
        </div>

        <div className="mt-auto space-y-1 border-t pt-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Route className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {flowNames.length > 0 ? `Rings in ${flowNames.join(", ")}` : "Not used in a call flow yet"}
            </span>
          </div>
          {team.createdAt && <div>Created {format(new Date(team.createdAt), "MMM d, yyyy")}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
